import styled from "@emotion/styled";
import { ThemeKey, motion } from "@smartsheet/design-tokens";
import { RequiredThemeKeys } from "@smartsheet/sds-theme-internal";
import React from "react";
import { forwardRef } from "react";
import { sizes } from "../../../constants/spacing";
import { useTheme } from "../../../theme/utils/useTheme";
import { scrollAreaTheme } from "../scrollAreaTheme";
import { ScrollAreaGutter } from "../types";
import { resolveGutterLength } from "../utils/resolveGutterLength";

export interface KeylineProps {
    /**
     * gutter is the amount of margin that should be applied to the Keyline component.
     */
    gutter: ScrollAreaGutter;

    /**
     * Is the keyline currently visible?
     */
    isVisible: boolean;

    /**
     * If true, the keyline will render along the top edge of its parent instead of the bottom
     * Defaults to false.
     */
    alignTop?: boolean;

    /**
     * The direction of the scolling container's orientation. Defaults to vertical.
     */
    direction?: "horizontal" | "vertical";

    /**
     * If true, the keyline will render a scrolling indication when the user scrolls. This is
     * theme-dependent but if used by the theme, will render something different when the user is
     * scrolling the ScrollArea.
     *
     * Defaults to true. Note this does nothing in the Lodestar or legacy themes.
     */
    includeScrollingIndication?: boolean;

    /**
     * If set to true, the keyline will be rendered differently to indicate that the user is
     * scrolling. Ideally this would be a required prop, but it's optional for backwards
     * compatibility.
     */
    isScrolling?: boolean;
}

export const KEYLINE_WIDTH_PROPERTY_NAME = `--smar--lodestar-core--keyline-width-as9dpu`;
export const KEYLINE_HEIGHT_PROPERTY_NAME = `--smar--lodestar-core--keyline-height-as9dpu`;

export const Keyline = forwardRef<HTMLDivElement, KeylineProps>(
    ({ alignTop, direction = "vertical", ...props }, ref) => {
        const { key } = useTheme();
        const themeMode = key as ThemeKey;
        return (
            <KeylineStyled
                {...props}
                ref={ref}
                alignment={alignTop ? "start" : "end"}
                themeMode={themeMode}
                direction={direction}
            />
        );
    }
);

Keyline.displayName = "Keyline";

interface KeylineStyledProps extends Omit<KeylineProps, "alignTop" | "direction"> {
    alignment: "start" | "end";
    // Required here.
    direction: "horizontal" | "vertical";
    themeMode: RequiredThemeKeys;
}

const KeylineStyled = styled("div", {
    shouldForwardProp: (propName) => {
        switch (propName) {
            case "direction":
            case "alignment":
            case "gutter":
            case "themeMode":
            case "includeScrollingIndication":
            case "isVisible":
            case "isScrolling":
                return false;
            default:
                return true;
        }
    },
})<KeylineStyledProps>`
    position: absolute;
    pointer-events: none;

    ${({ direction, alignment }) => {
        switch (alignment) {
            case "start":
                return direction === "vertical" ? `top: 0` : `left: 0`;
            case "end":
                return direction === "vertical" ? `bottom: 0` : `right: 0`;
        }
    }};

    margin-left: ${({ direction, gutter }) => {
        // Don't support gutter in horizontal mode. We'd rather not support the gutter prop at all
        // since it's generally not what you want.
        if (direction === "horizontal") {
            return "none";
        }

        switch (gutter) {
            case "none":
                return "0px";
            case "legacy":
                return `${sizes.xLarge}px`;
            default:
                return resolveGutterLength(gutter);
        }
    }};

    //Animate the rendering of the keyline
    opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};
    transition: opacity ${motion.animation.default};

    height: var(${KEYLINE_HEIGHT_PROPERTY_NAME}, auto);
    width: var(${KEYLINE_WIDTH_PROPERTY_NAME}, auto);

    // This hides this element, but allows the box-shadow to still be visible.
    overflow: hidden;

    // This provides the box-shadow for the keyline.
    &::before {
        content: "";
        display: block;

        transform: ${getTransform};
        box-shadow: ${getBoxShadow};

        // TODO use tokens for this
        transition: box-shadow 450ms cubic-bezier(0.3, 0.3, 0.2, 1) 0s, margin 450ms cubic-bezier(0.3, 0.3, 0.2, 1) 0s,
            width 450ms cubic-bezier(0.3, 0.3, 0.2, 1) 0s, height 450ms cubic-bezier(0.3, 0.3, 0.2, 1) 0s;
        opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};

        // This dimension length allows the box-shadow to be properly offset from its parent.
        // This value depends on the box-shadow's blur and offset values.
        ${({ direction, isScrolling, includeScrollingIndication }) => {
            switch (direction) {
                case "vertical":
                    return `
                        height: 16px;
                        width: ${isScrolling && includeScrollingIndication ? "calc(100% - 8px)" : "100%"};
                        margin-left: ${isScrolling && includeScrollingIndication ? "4px" : "0"};
                    `;
                case "horizontal":
                    return `
                        height: ${isScrolling && includeScrollingIndication ? "calc(100% - 8px)" : "100%"};
                        width: 16px;
                        margin-top: ${isScrolling && includeScrollingIndication ? "4px" : "0"};
                    `;
            }
        }}
    }
`;

function getBoxShadow({
    themeMode,
    alignment,
    direction,
    isScrolling,
    includeScrollingIndication,
}: KeylineStyledProps) {
    return includeScrollingIndication && isScrolling
        ? scrollAreaTheme.keyline[alignment].boxShadow[direction].scrolling(themeMode)
        : scrollAreaTheme.keyline[alignment].boxShadow[direction].rest(themeMode);
}

function getTransform({ direction, alignment }: KeylineStyledProps) {
    switch (direction) {
        case "vertical":
            return alignment === "start" ? "translateY(-16px)" : "translateY(16px)";
        case "horizontal":
            return alignment === "start" ? "translateX(-16px)" : "translateX(16px)";
    }
}
